import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { differenceInDays } from "date-fns";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Check, X, Sparkles, Rocket, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { 
  formatDiscountMessage, 
  saveCouponData, 
  clearCouponData,
  getFormattedProductList,
  validateCouponGlobally,
  hasValidCoupons,
  PRODUCT_NAMES,
  type CouponResult 
} from "@/lib/couponUtils";

interface PackageInfo {
  id: "spotlight" | "social_boost";
  price: number;
  icon: typeof Sparkles;
  bgColor: string;
  borderColor: string;
}

const packages: PackageInfo[] = [
  { id: "spotlight", price: 149, icon: Sparkles, bgColor: "bg-amber-400", borderColor: "border-amber-600" },
  { id: "social_boost", price: 399, icon: Rocket, bgColor: "bg-violet-400", borderColor: "border-violet-600" },
];

const VisibilityPackages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, hasCompletedScreening, screeningStatus, user } = useAuth();
  const [checkingOutPackage, setCheckingOutPackage] = useState<string | null>(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupons, setAppliedCoupons] = useState<Record<string, CouponResult | null>>({});
  
  // Active packages state
  const [hasActiveSpotlight, setHasActiveSpotlight] = useState(false);
  const [hasActiveSocialBoost, setHasActiveSocialBoost] = useState(false);
  const [spotlightEndDate, setSpotlightEndDate] = useState<Date | null>(null);
  const [socialBoostEndDate, setSocialBoostEndDate] = useState<Date | null>(null);

  const featureRows = [
    { key: "homepageVisibility", spotlight: "28", socialBoost: "28", isTime: true },
    { key: "spotlightBadge", spotlight: true, socialBoost: true },
    { key: "searchPriority", spotlight: true, socialBoost: true },
    { key: "socialAds", spotlight: false, socialBoost: true },
    { key: "dedicatedManager", spotlight: false, socialBoost: true },
    { key: "campaignReport", spotlight: false, socialBoost: true },
  ];

  // Check for active packages
  useEffect(() => {
    const checkActivePackages = async () => {
      if (!user?.id) return;
      
      try {
        // Check active spotlight
        const { data: spotlightData } = await supabase
          .from('featured_professionals')
          .select('end_date')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .eq('feature_type', 'spotlight')
          .gte('end_date', new Date().toISOString())
          .maybeSingle();
        
        if (spotlightData?.end_date) {
          setHasActiveSpotlight(true);
          setSpotlightEndDate(new Date(spotlightData.end_date));
        }
        
        // Check active social_boost subscription
        const { data: socialBoostData } = await supabase
          .from('subscriptions')
          .select('current_period_end')
          .eq('user_id', user.id)
          .eq('product_type', 'social_boost')
          .eq('status', 'active')
          .gte('current_period_end', new Date().toISOString())
          .maybeSingle();
        
        if (socialBoostData?.current_period_end) {
          setHasActiveSocialBoost(true);
          setSocialBoostEndDate(new Date(socialBoostData.current_period_end));
        }
      } catch (error) {
        console.error('Error checking active packages:', error);
      }
    };
    
    checkActivePackages();
  }, [user?.id]);

  // Read coupon from URL or sessionStorage on mount
  useEffect(() => {
    const couponFromUrl = searchParams.get('coupon');
    const savedCoupon = sessionStorage.getItem('pendingCoupon');
    const savedCouponData = sessionStorage.getItem('appliedCouponData');
    
    if (couponFromUrl) {
      // Coupon from URL = first visit, show toast
      setCouponCode(couponFromUrl.toUpperCase());
      validateCouponForAll(couponFromUrl.toUpperCase(), true);
    } else if (savedCoupon) {
      setCouponCode(savedCoupon);
      
      // Try to load saved data silently
      if (savedCouponData) {
        try {
          const parsedData = JSON.parse(savedCouponData);
          // Extract only relevant packages for this page
          const relevantData: Record<string, CouponResult | null> = {};
          ['spotlight', 'social_boost'].forEach(pkg => {
            if (parsedData[pkg]) {
              relevantData[pkg] = parsedData[pkg];
            }
          });
          setAppliedCoupons(relevantData);
          // No toast - just restore data
        } catch (e) {
          validateCouponForAll(savedCoupon, false);
        }
      } else {
        validateCouponForAll(savedCoupon, false);
      }
    }
  }, []);

  const validateCouponForAll = async (code: string, showToast = true) => {
    setCouponLoading(true);
    
    try {
      // Validate against ALL product types globally
      const allResults = await validateCouponGlobally(supabase, code);
      
      // Check if any product is valid globally
      const hasAnyValid = hasValidCoupons(allResults);
      
      if (hasAnyValid) {
        // Save ALL results to sessionStorage
        saveCouponData(code, allResults);
        setAppliedCoupons(allResults);
        
        if (showToast) {
          const locale = i18n.language || 'it';
          const formattedProducts = getFormattedProductList(allResults, locale);
          toast.success(t("pricing.coupon.validDynamic", { 
            products: formattedProducts,
            discount: formatDiscountMessage(
              allResults[Object.keys(allResults).find(k => allResults[k]?.valid) || '']?.discountType || 'percentage',
              allResults[Object.keys(allResults).find(k => allResults[k]?.valid) || '']?.discountValue || 0
            )
          }));
        }
      } else {
        setAppliedCoupons({});
        if (showToast) {
          toast.error(t("pricing.coupon.invalid"));
        }
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      if (showToast) {
        toast.error(t("pricing.coupon.invalid"));
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    validateCouponForAll(couponCode.trim().toUpperCase(), true);
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedCoupons({});
    sessionStorage.removeItem('pendingCoupon');
    sessionStorage.removeItem('appliedCouponData');
  };

  const calculateFinalPrice = (pkg: PackageInfo): number => {
    const coupon = appliedCoupons[pkg.id];
    if (!coupon) return pkg.price;
    
    if (coupon.discountType === 'fixed') {
      return Math.max(0, pkg.price - coupon.discountValue);
    } else if (coupon.discountType === 'full') {
      return 0;
    } else {
      return pkg.price * (1 - coupon.discountPercent / 100);
    }
  };

  const getDiscountMessage = (pkg: PackageInfo): string | null => {
    const coupon = appliedCoupons[pkg.id];
    if (!coupon) return null;
    
    if (coupon.discountType === 'full') {
      return t('pricing.coupon.valid100');
    } else if (coupon.discountType === 'fixed') {
      return t('pricing.coupon.validFixedDiscount', { amount: coupon.discountValue });
    } else {
      return t('pricing.coupon.validDiscount', { percent: coupon.discountPercent });
    }
  };

  const handleCheckout = async (pkg: PackageInfo) => {
    if (!isAuthenticated) {
      toast.error(t("pricing.errors.loginRequired"));
      navigate("/auth/login");
      return;
    }

    if (!hasCompletedScreening || screeningStatus !== "approved") {
      toast.error(t("pricing.error.screeningRequired"));
      navigate("/become-professional");
      return;
    }

    setCheckingOutPackage(pkg.id);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          planType: pkg.id,
          billingType: "one_time_28_days",
          couponCode: appliedCoupons[pkg.id] ? couponCode : undefined,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message);
      } else if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast.error(error.message || t("visibilityPackages.checkoutError"));
    } finally {
      setCheckingOutPackage(null);
    }
  };

  const renderCellValue = (value: boolean | string, isTime?: boolean) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className="w-5 h-5 text-primary mx-auto" />
      ) : (
        <X className="w-5 h-5 text-muted-foreground/50 mx-auto" />
      );
    }
    if (isTime) {
      return <span className="font-medium">{value} {t("visibilityPackages.table.days")}</span>;
    }
    return <span className="font-medium">{value}</span>;
  };

  const hasAnyCouponApplied = Object.values(appliedCoupons).some(Boolean);

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO
        title={t("visibilityPackages.seoTitle")}
        description={t("visibilityPackages.seoDescription")}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <SEOBreadcrumb items={[
              { label: t('pricing.breadcrumb'), href: '/pricing' },
              { label: t('visibilityPackages.breadcrumb') }
            ]} />
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-dark mb-4">
              {t("visibilityPackages.heroTitle")}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("visibilityPackages.heroSubtitle")}
            </p>
          </div>

          {/* Coupon Input Section */}
          <BrutalCard className="max-w-xl mx-auto mb-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <BrutalInput
                placeholder={t("pricing.coupon.placeholder")}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1"
                disabled={hasAnyCouponApplied}
              />
              {!hasAnyCouponApplied ? (
                <BrutalButton
                  onClick={handleApplyCoupon}
                  disabled={couponLoading}
                  className="whitespace-nowrap"
                >
                  {couponLoading ? t('pricing.coupon.validating') : t('pricing.coupon.apply')}
                </BrutalButton>
              ) : (
                <BrutalButton
                  onClick={handleRemoveCoupon}
                  variant="outline"
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t("pricing.coupon.remove")}
                </BrutalButton>
              )}
            </div>
            {hasAnyCouponApplied && (() => {
              const sample = Object.values(appliedCoupons).find(Boolean);
              if (!sample) return null;
              const discount = formatDiscountMessage(sample.discountType, sample.discountValue);
              const locale = i18n.language || 'it';
              const formattedProducts = getFormattedProductList(appliedCoupons, locale);
              return (
                <div className="mt-4 p-4 bg-primary/10 border-[3px] border-primary">
                  <p className="font-heading font-bold text-dark">
                    {t('pricing.coupon.couponConfirmation', { discount })}
                  </p>
                  <p className="font-body text-sm text-muted-foreground mt-1">
                    {t('pricing.coupon.appliesToFormatted', { products: formattedProducts })}
                  </p>
                  {sample.couponDescription && (
                    <p className="font-body text-sm text-muted-foreground mt-1">
                      {sample.couponDescription}
                    </p>
                  )}
                </div>
              );
            })()}
          </BrutalCard>

          {/* Package Cards Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {packages.map((pkg) => {
              const Icon = pkg.icon;
              const coupon = appliedCoupons[pkg.id];
              const finalPrice = calculateFinalPrice(pkg);
              const discountMessage = getDiscountMessage(pkg);
              
              // Check if this package is active
              const isActive = pkg.id === 'spotlight' ? hasActiveSpotlight : hasActiveSocialBoost;
              const endDate = pkg.id === 'spotlight' ? spotlightEndDate : socialBoostEndDate;
              const daysRemaining = endDate ? differenceInDays(endDate, new Date()) : 0;
              
              return (
                <BrutalCard key={pkg.id} className={`text-center ${isActive ? 'border-primary bg-primary/5' : ''}`}>
                  <div className={`w-16 h-16 mx-auto mb-4 ${pkg.bgColor} ${pkg.borderColor} border-[3px] flex items-center justify-center`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-dark mb-2">
                    {t(`visibilityPackages.packages.${pkg.id}.name`)}
                  </h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t(`visibilityPackages.packages.${pkg.id}.tagline`)}
                  </p>
                  
                  {(() => {
                    // Determine mutual exclusion
                    const otherPackageActive = pkg.id === 'spotlight' ? hasActiveSocialBoost : hasActiveSpotlight;
                    const otherPackageName = pkg.id === 'spotlight' 
                      ? t("dashboard.upsell.packages.social_boost.name")
                      : t("dashboard.upsell.packages.spotlight.name");
                    const otherPackageEndDate = pkg.id === 'spotlight' ? socialBoostEndDate : spotlightEndDate;
                    const otherDaysRemaining = otherPackageEndDate 
                      ? differenceInDays(otherPackageEndDate, new Date())
                      : 0;

                    if (isActive) {
                      return (
                        <>
                          <BrutalBadge variant="default" className="bg-primary text-primary-foreground mb-2">
                            {t("dashboard.upsell.alreadyActive")}
                          </BrutalBadge>
                          <p className="text-sm text-primary font-medium">
                            {t("dashboard.upsell.activeFor", { days: daysRemaining })}
                          </p>
                        </>
                      );
                    }
                    
                    if (otherPackageActive) {
                      return (
                        <>
                          <BrutalBadge variant="secondary" className="mb-2">
                            {t("dashboard.upsell.otherPackageActiveFor", { 
                              package: otherPackageName, 
                              days: otherDaysRemaining 
                            })}
                          </BrutalBadge>
                          <BrutalButton className="w-full mt-4" variant="secondary" disabled>
                            {t("dashboard.upsell.otherPackageActive", { package: otherPackageName })}
                          </BrutalButton>
                        </>
                      );
                    }

                    return (
                      <>
                        <div className="font-heading font-bold text-3xl text-dark">
                          {coupon ? (
                            <>
                              <span className="text-lg line-through text-muted-foreground mr-2">
                                €{pkg.price}
                              </span>
                              <span className="text-primary">€{finalPrice.toFixed(0)}</span>
                            </>
                          ) : (
                            <>€{pkg.price}</>
                          )}
                          <span className="text-base font-normal text-muted-foreground ml-1">
                            / 28 {t("visibilityPackages.table.days")}
                          </span>
                        </div>
                        {discountMessage && (
                          <p className="text-sm text-primary mt-2 font-medium">
                            {discountMessage}
                          </p>
                        )}
                        {hasAnyCouponApplied && !coupon && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {t("visibilityPackages.coupon.notApplicable")}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </BrutalCard>
              );
            })}
          </div>

          {/* Comparison Table */}
          <BrutalCard className="overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-dark">
                    <th className="text-left p-4 font-heading font-bold text-dark">
                      {t("visibilityPackages.table.feature")}
                    </th>
                    <th className="text-center p-4 font-heading font-bold text-dark min-w-[140px]">
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        {t("visibilityPackages.packages.spotlight.name")}
                      </div>
                    </th>
                    <th className="text-center p-4 font-heading font-bold text-dark min-w-[140px]">
                      <div className="flex items-center justify-center gap-2">
                        <Rocket className="w-5 h-5 text-violet-500" />
                        {t("visibilityPackages.packages.social_boost.name")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featureRows.map((row, index) => (
                    <tr
                      key={row.key}
                      className={index < featureRows.length - 1 ? "border-b border-border" : ""}
                    >
                      <td className="p-4">
                        <div className="font-body font-medium text-dark">
                          {t(`visibilityPackages.table.features.${row.key}.label`)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t(`visibilityPackages.table.features.${row.key}.description`)}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {renderCellValue(row.spotlight, row.isTime)}
                      </td>
                      <td className="p-4 text-center">
                        {renderCellValue(row.socialBoost, row.isTime)}
                      </td>
                    </tr>
                  ))}
                  {/* Price Row */}
                  <tr className="border-t-[3px] border-dark bg-muted/30">
                    <td className="p-4 font-heading font-bold text-dark">
                      {t("visibilityPackages.table.price")}
                    </td>
                    <td className="p-4 text-center font-heading font-bold text-2xl text-dark">
                      {hasActiveSpotlight ? (
                        <span className="text-primary text-base">
                          {t("dashboard.upsell.activeFor", { days: spotlightEndDate ? differenceInDays(spotlightEndDate, new Date()) : 0 })}
                        </span>
                      ) : hasActiveSocialBoost ? (
                        <span className="text-muted-foreground text-sm">
                          {t("dashboard.upsell.otherPackageActive", { package: t("dashboard.upsell.packages.social_boost.name") })}
                        </span>
                      ) : appliedCoupons.spotlight ? (
                        <>
                          <span className="text-base line-through text-muted-foreground mr-1">€149</span>
                          <span className="text-primary">€{calculateFinalPrice(packages[0]).toFixed(0)}</span>
                        </>
                      ) : (
                        "€149"
                      )}
                    </td>
                    <td className="p-4 text-center font-heading font-bold text-2xl text-dark">
                      {hasActiveSocialBoost ? (
                        <span className="text-primary text-base">
                          {t("dashboard.upsell.activeFor", { days: socialBoostEndDate ? differenceInDays(socialBoostEndDate, new Date()) : 0 })}
                        </span>
                      ) : hasActiveSpotlight ? (
                        <span className="text-muted-foreground text-sm">
                          {t("dashboard.upsell.otherPackageActive", { package: t("dashboard.upsell.packages.spotlight.name") })}
                        </span>
                      ) : appliedCoupons.social_boost ? (
                        <>
                          <span className="text-base line-through text-muted-foreground mr-1">€399</span>
                          <span className="text-primary">€{calculateFinalPrice(packages[1]).toFixed(0)}</span>
                        </>
                      ) : (
                        "€399"
                      )}
                    </td>
                  </tr>
                  {/* CTA Row */}
                  <tr>
                    <td className="p-4"></td>
                    <td className="p-4 text-center">
                      {hasActiveSpotlight ? (
                        <BrutalBadge variant="default" className="bg-primary text-primary-foreground">
                          {t("dashboard.upsell.alreadyActive")}
                        </BrutalBadge>
                      ) : hasActiveSocialBoost ? (
                        <BrutalButton variant="secondary" className="w-full max-w-[160px]" disabled>
                          {t("dashboard.upsell.otherPackageActive", { package: t("dashboard.upsell.packages.social_boost.name") })}
                        </BrutalButton>
                      ) : (
                        <BrutalButton
                          onClick={() => handleCheckout(packages[0])}
                          variant="outline"
                          className="w-full max-w-[160px]"
                          disabled={checkingOutPackage !== null}
                        >
                          {checkingOutPackage === "spotlight" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            t("visibilityPackages.buySpotlight")
                          )}
                        </BrutalButton>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {hasActiveSocialBoost ? (
                        <BrutalBadge variant="default" className="bg-primary text-primary-foreground">
                          {t("dashboard.upsell.alreadyActive")}
                        </BrutalBadge>
                      ) : hasActiveSpotlight ? (
                        <BrutalButton variant="secondary" className="w-full max-w-[160px]" disabled>
                          {t("dashboard.upsell.otherPackageActive", { package: t("dashboard.upsell.packages.spotlight.name") })}
                        </BrutalButton>
                      ) : (
                        <BrutalButton
                          onClick={() => handleCheckout(packages[1])}
                          className="w-full max-w-[160px]"
                          disabled={checkingOutPackage !== null}
                        >
                          {checkingOutPackage === "social_boost" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            t("visibilityPackages.buySocialBoost")
                          )}
                        </BrutalButton>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BrutalCard>

          {/* Back to Pricing Link */}
          <div className="text-center">
            <BrutalButton variant="outline" onClick={() => navigate("/pricing")}>
              {t("visibilityPackages.backToPricing")}
            </BrutalButton>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VisibilityPackages;